import { useState, type ReactNode } from "react";

import { DialogoCambiosSinGuardar } from "@/components/DialogoCambiosSinGuardar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";

interface DialogoDeEdicionDeFilaProps {
  /** Está abierto. La pestaña lo deriva de su propio `editando !== null`. */
  abierto: boolean;
  /** `Editar "Tractor John Deere 6110"`. Lo arma la pestaña: solo ella sabe cómo se llama una fila suya. */
  titulo: string;
  /** Una operación de fila está en vuelo (alta, corrección, borrado, foto). */
  ocupada: boolean;
  /** Falso mientras falte lo mínimo de la fila. Apaga el botón de guardar. */
  puedeGuardar: boolean;
  /** Hay TEXTO cambiado sin guardar. Decide el rótulo del cierre y si se confirma el descarte. */
  hayCambios: boolean;
  onGuardar: () => void;
  onCerrar: () => void;
  /** Los campos propios de la pestaña, más su bloque de fotos. */
  children: ReactNode;
}

/**
 * El modal genérico para editar UNA fila de un cuadro de pestaña técnica:
 * Maquinarias, Instalaciones, Construcciones y Plantaciones.
 *
 * NACIÓ PARA QUE EDITAR DEJE DE COMPARTIR EL PANEL DE ARRIBA. Antes, tocar
 * "Editar" en una fila hacía que el panel de alta —lejos de la fila que se
 * tocó, y compitiendo con "Guardar cambios" de la pestaña— cambiara de título
 * y de rótulo de botón. Con el cuadro largo, ese cambio no se notaba: el
 * clic en la fila nueve no movía nada que estuviera a la vista.
 *
 * LO QUE COMPARTEN LAS CUATRO PESTAÑAS ES EL MARCO —Dialog, título, cuerpo con
 * scroll, footer, la confirmación de descarte—; los campos propios de cada
 * una (`CamposDeMaquina`, `CamposDeInstalacion`, …) llegan como `children`,
 * igual que las columnas en `FilasPropuestas`.
 *
 * SIGUE EL PRECEDENTE DE `ModalDeCorreccion`: `Dialog` + `DialogContent
 * className="sm:max-w-2xl"`, no `AlertDialog` — acá SÍ se puede cerrar con
 * Escape o un clic afuera, y lo que hace que ese cierre no pierda datos en
 * silencio es la confirmación de abajo.
 */
export function DialogoDeEdicionDeFila({
  abierto,
  titulo,
  ocupada,
  puedeGuardar,
  hayCambios,
  onGuardar,
  onCerrar,
  children,
}: DialogoDeEdicionDeFilaProps) {
  /* Si hay que confirmar el descarte, esto se abre EN VEZ de cerrar. Es
     hermano del `Dialog`, no hijo: Radix soporta un `AlertDialog` anidado, y
     desmontar el `Dialog` para mostrar la confirmación se llevaría el
     formulario con él. */
  const [confirmandoCierre, setConfirmandoCierre] = useState(false);

  /* El punto único por el que pasan el botón de cierre, Escape y el clic
     afuera: los tres son "cerrar" y los tres tienen que respetar la misma
     regla. Sin cambios, cerrar cierra y punto; con cambios, lo que se abre es
     la confirmación — no `onCerrar` directo. */
  function pedirCierre() {
    if (hayCambios) {
      setConfirmandoCierre(true);
    } else {
      onCerrar();
    }
  }

  return (
    <>
      <Dialog open={abierto} onOpenChange={(siguiente) => !siguiente && pedirCierre()}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{titulo}</DialogTitle>
            <DialogDescription>
              Las fotos se guardan apenas las sueltas, sin esperar este botón. El resto de los
              campos se guarda recién al apretar «Guardar cambios».
            </DialogDescription>
          </DialogHeader>

          {/* CUERPO CON SCROLL, no el formulario entero. Construcciones llega
              a once campos más las fotos, y sin un tope el modal se saldría
              de la pantalla. NO es `ScrollArea` del DS: hay una nota del
              repo de que desborda con `max-h`. El padding lateral evita que
              el anillo de foco de los controles del primer/último campo
              quede recortado contra el borde del contenedor. */}
          <div className="max-h-[60vh] space-y-4 overflow-y-auto px-1">{children}</div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={pedirCierre} disabled={ocupada}>
              {/* "Cancelar" promete deshacer algo; sin cambios de texto
                  pendientes no hay nada que deshacer —y al lado puede haber
                  fotos que SÍ se subieron—, así que decir "Cancelar" ahí
                  sería una promesa falsa. */}
              {hayCambios ? "Cancelar" : "Cerrar"}
            </Button>
            <Button type="button" onClick={onGuardar} disabled={ocupada || !puedeGuardar}>
              {ocupada ? <Spinner /> : null}
              Guardar cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DialogoCambiosSinGuardar
        abierto={confirmandoCierre}
        descripcion={
          "Vas a perder los cambios sin guardar en esta fila. Las fotos que ya se " +
          "subieron no se deshacen: siguen en el informe aunque descartes el resto."
        }
        guardando={ocupada}
        // ACÁ SÍ HAY DÓNDE GUARDAR (el PUT de la fila), a diferencia de un
        // alta a medio llenar que todavía no tiene id: se ofrecen las TRES
        // salidas y no solo cancelar/descartar.
        onGuardarYSalir={() => {
          setConfirmandoCierre(false);
          onGuardar();
        }}
        onSalirSinGuardar={() => {
          setConfirmandoCierre(false);
          onCerrar();
        }}
        onCancelar={() => setConfirmandoCierre(false)}
      />
    </>
  );
}

/**
 * Compara dos objetos planos de strings campo a campo.
 *
 * EXISTE PORQUE LOS CUATRO `FormularioDeX` SON OBJETOS PLANOS DE STRINGS: una
 * comparación superficial alcanza, y evita un `JSON.stringify` que
 * dependería del orden en que se declararon las claves —dos objetos con las
 * mismas claves en distinto orden serían "distintos" para `stringify` y
 * "iguales" acá, que es lo correcto.
 */
export function mismosCampos<T extends object>(a: T, b: T): boolean {
  return (Object.keys(a) as (keyof T)[]).every((clave) => a[clave] === b[clave]);
}
